import { stateVAExemptions } from '@/types/vaExemptions';

export interface ExemptionResult {
  baseExemption: number;
  age65Bonus: number;
  totalExemption: number;
  taxableValue: number;
  description: string;
  stateName: string;
}

export const calculateStateVAExemption = (
  state: string,
  vaDisabilityStatus: "none" | "partial" | "full" | null,
  vaDisabilityRatingPercentage: number | null,
  veteranAge: number | null,
  homePrice: number
): ExemptionResult => {
  const defaultResult: ExemptionResult = {
    baseExemption: 0,
    age65Bonus: 0,
    totalExemption: 0,
    taxableValue: homePrice,
    description: 'No exemption available',
    stateName: state
  };

  if (!vaDisabilityStatus || vaDisabilityStatus === 'none') {
    return defaultResult;
  }

  // 100% disability - check state rules
  if (vaDisabilityStatus === 'full') {
    const stateData = stateVAExemptions[state];
    if (!stateData) {
      return {
        ...defaultResult,
        baseExemption: homePrice,
        totalExemption: homePrice,
        taxableValue: 0,
        description: 'Full property tax exemption (100% VA disability - universal benefit)'
      };
    }
    
    const fullTier = stateData.ratingTiers.find(t => t.minRating === 100 || (t.minRating <= 100 && t.maxRating === 100));
    
    if (fullTier) {
      if (fullTier.exemptionPercentage === 100) {
        return {
          ...defaultResult,
          baseExemption: homePrice,
          totalExemption: homePrice,
          taxableValue: 0,
          description: `${stateData.state}: ${fullTier.description}`,
          stateName: stateData.state
        };
      } else if (fullTier.exemptionAmount) {
        const exemption = Math.min(fullTier.exemptionAmount, homePrice);
        return {
          ...defaultResult,
          baseExemption: exemption,
          totalExemption: exemption,
          taxableValue: Math.max(0, homePrice - exemption),
          description: `${stateData.state}: ${fullTier.description}`,
          stateName: stateData.state
        };
      }
    }
    
    // Default to full exemption if not specifically configured
    return {
      ...defaultResult,
      baseExemption: homePrice,
      totalExemption: homePrice,
      taxableValue: 0,
      description: `${stateData.state}: Full exemption for 100% disability`,
      stateName: stateData.state
    };
  }

  // Partial disability - state-specific tiers
  if (vaDisabilityStatus === 'partial' && vaDisabilityRatingPercentage) {
    const stateData = stateVAExemptions[state];
    if (!stateData) {
      return {
        ...defaultResult,
        description: `${state}: VA property tax exemption details not available. Contact your county assessor.`
      };
    }
    
    const tier = stateData.ratingTiers.find(
      t => vaDisabilityRatingPercentage >= t.minRating && 
           vaDisabilityRatingPercentage <= t.maxRating
    );
    
    if (!tier) {
      return {
        ...defaultResult,
        description: `${stateData.state}: No exemption available for ${vaDisabilityRatingPercentage}% rating`
      };
    }
    
    let baseExemption = 0;
    
    if (tier.exemptionAmount) {
      baseExemption = tier.exemptionAmount;
    } else if (tier.exemptionPercentage) {
      baseExemption = (homePrice * tier.exemptionPercentage) / 100;
    }
    
    let age65Bonus = 0;
    
    // Check for age-based bonus
    if (veteranAge && veteranAge >= 65 && stateData.specialProvisions.age65Plus) {
      age65Bonus = stateData.specialProvisions.age65Plus.additionalExemption;
    }
    
    const totalExemption = Math.min(baseExemption + age65Bonus, homePrice);
    
    return {
      baseExemption,
      age65Bonus,
      totalExemption,
      taxableValue: Math.max(0, homePrice - totalExemption),
      description: `${stateData.state}: ${tier.description}${age65Bonus > 0 ? ' + Age 65+ bonus' : ''}`,
      stateName: stateData.state
    };
  }

  return defaultResult;
};

export const getAvailableStatesForVAExemptions = (): string[] => {
  return Object.keys(stateVAExemptions);
};

export const getStateExemptionDetails = (state: string) => {
  return stateVAExemptions[state] || null;
};
